import React from 'react';
import { useSettings } from '../contexts/SettingsContext';
import {
  VarnasaaraData,
  VarnasaaraComponent,
  ComponentDescription,
} from '../types';
import '../styles/VarnasaaraVisualization.css';

interface VarnasaaraVisualizationProps {
  data: VarnasaaraData | null;
  loading: boolean;
  error: string | null;
}

type BadgeType = 'Secondary' | 'Info' | 'Primary' | 'Success' | 'Dark';

const VarnasaaraVisualization: React.FC<VarnasaaraVisualizationProps> = ({
  data,
  loading,
  error,
}) => {
  const { scaledFontSize } = useSettings();

  if (loading) {
    return (
      <div className="varnasaara-loading">
        <div className="loading-spinner"></div>
        <span className="loading-text">विश्लेषणम्...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="varnasaara-error">
        <h4 className="error-title">त्रुटिः (Error)</h4>
        <p className="error-text">{error}</p>
      </div>
    );
  }

  if (!data || !data.syllables || data.syllables.length === 0) {
    return null;
  }

  const renderBadge = (text: string, type: BadgeType) => (
    <span key={`${text}-${type}`} className={`badge badge-${type.toLowerCase()}`}>
      {text}
    </span>
  );

  const isStructuredDescription = (desc: string | ComponentDescription): desc is ComponentDescription => {
    return typeof desc === 'object' && desc !== null;
  };

  const renderComponent = (
    component: VarnasaaraComponent,
    syllableIndex: number,
    componentIndex: number
  ) => {
    const description = component.description;
    const hasStructuredDescription =
      component.type === 'vowel' && isStructuredDescription(description) && description.preSvara;

    return (
      <div
        key={`${syllableIndex}-${componentIndex}`}
        className={`component-card ${component.type}`}
      >
        <div className="component-header">
          <span
            className="component-label"
            style={{ fontSize: scaledFontSize(20) }}
          >
            {component.character}
          </span>
          <div className="badge-container">
            {component.metadata?.maatra &&
              renderBadge(component.metadata.maatra, 'Secondary')}
            {component.metadata?.anga &&
              renderBadge(component.metadata.anga, 'Info')}
            {component.metadata?.svara &&
              renderBadge(component.metadata.svara, 'Primary')}
            {component.metadata?.hasDwitva && renderBadge('द्वित्व', 'Success')}
            {component.metadata?.specialMarkers?.map((marker, idx) =>
              renderBadge(marker, 'Dark')
            )}
          </div>
        </div>

        <div className="component-body">
          {hasStructuredDescription ? (
            <div>
              <p
                className="description-text"
                style={{
                  fontSize: scaledFontSize(17),
                  lineHeight: `${scaledFontSize(26)}px`,
                }}
              >
                {(description as ComponentDescription).preSvara || ''}
              </p>
              {(description as ComponentDescription).svara && (
                <p
                  className="svara-text"
                  style={{
                    fontSize: scaledFontSize(17),
                    lineHeight: `${scaledFontSize(26)}px`,
                  }}
                >
                  {(description as ComponentDescription).svara}
                </p>
              )}
              <p
                className="description-text"
                style={{
                  fontSize: scaledFontSize(17),
                  lineHeight: `${scaledFontSize(26)}px`,
                }}
              >
                {(description as ComponentDescription).postSvara || ''}
              </p>
            </div>
          ) : (
            <p
              className="description-text"
              style={{
                fontSize: scaledFontSize(17),
                lineHeight: `${scaledFontSize(26)}px`,
              }}
            >
              {typeof description === 'string'
                ? description
                : (description as ComponentDescription)?.full || '\u25cc'}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="varnasaara-container">
      <div className="varnasaara-header">
        <h3 className="varnasaara-title">वर्णसार-वर्णक्रमः</h3>
      </div>

      <div className="varnasaara-word-display">
        <p
          className="varnasaara-word-text"
          style={{ fontSize: scaledFontSize(22) }}
        >
          {data.word}
        </p>
      </div>

      {data.syllables.map((syllable, syllableIndex) => (
        <div key={syllableIndex} className="syllable-block">
          <p
            className="syllable-text"
            style={{ fontSize: scaledFontSize(20) }}
          >
            {syllable.text}
          </p>

          {syllable.components.map((component, componentIndex) =>
            renderComponent(component, syllableIndex, componentIndex)
          )}

          {syllable.virama && (
            <div className="virama-container">
              <p
                className="virama-text"
                style={{
                  fontSize: scaledFontSize(20),
                  lineHeight: `${scaledFontSize(28)}px`,
                }}
              >
                {syllable.virama}
              </p>
            </div>
          )}
        </div>
      ))}

      {data.finalVirama && (
        <div className="final-virama-container">
          <p
            className="virama-text"
            style={{
              fontSize: scaledFontSize(20),
              lineHeight: `${scaledFontSize(28)}px`,
            }}
          >
            {data.finalVirama}
          </p>
        </div>
      )}
    </div>
  );
};

export default VarnasaaraVisualization;
